import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Link,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Divider,
} from '@mui/material';

import PersonIcon from '@mui/icons-material/Person';

import { fetchPlayerRanking } from './RankingApi.jsx';

const getPlayerPhoto = (player) => {
  if (player?.photoUrl) return player.photoUrl;
  return '';
};

const buildPlayerUrl = (player) => {
  if (!player?._id) return null;

  const playerLabel =
    player?.displayName ||
    [player?.firstname, player?.lastname].filter(Boolean).join(' ') ||
    player?._id;

  return `/dsas-view/joueur/${encodeURIComponent(playerLabel)}_${player._id}`;
};

const buildTeamUrl = (team) => {
  if (!team?._id) return null;

  const teamLabel =
    team?.name ||
    team?.fullName ||
    team?._id;

  return `/dsas-view/equipe/${encodeURIComponent(teamLabel)}_${team._id}`;
};

const formatDecimal = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

const getColumnsByStatType = (statType) => {
  switch (statType) {
    case 'tries':
      return {
        mainLabel: 'essais',
        ratioLabel: 'essais / match',
      };
    case 'scorers':
      return {
        mainLabel: 'points',
        ratioLabel: 'points / match',
      };
    case 'goals':
      return {
        mainLabel: 'buts',
        ratioLabel: 'buts / match',
      };
    case 'assists':
      return {
        mainLabel: 'passes',
        ratioLabel: 'passes / match',
      };
    case 'stars':
    default:
      return {
        mainLabel: 'étoiles',
        ratioLabel: 'étoiles / match',
      };
  }
};

export default function RankingSection({
  provider,
  statType,
  discipline,
  competitionIds = [],
  season,
  widgetConfigurationId=null,
  token,
  baseUrl = '',
  limit = 10,
  sort = 'value_desc',
  onNavigate,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({
    items: [],
    total: 0,
    totalPages: 0,
    page: 1,
    limit,
  });

  const labels = useMemo(() => getColumnsByStatType(statType), [statType]);

  const competitionKey = competitionIds?.join(',') || '';

  useEffect(() => {
    let ignore = false;

    const load = async () => {

      try {
        setLoading(true);
        setError('');

        const result = await fetchPlayerRanking({
          provider,
          discipline,
          statType,
          competitionIds,
          season,
          widgetConfigurationId,
          page,
          limit,
          sort,
        });

        if (!ignore) {
          setData(result);
        }
      } catch (e) {
        if (!ignore) {
          setError("Impossible de charger le classement.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [page, baseUrl, token, provider, discipline, statType, competitionKey, season, limit, sort]);

  


useEffect(() => {
  setPage(1);
}, [provider, statType, discipline, season, competitionKey]);

  const handleLinkClick = (event, url) => {
    if (!url || !onNavigate) return;
    event.preventDefault();
    onNavigate(url);
  };

  const renderDesktopTable = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 52 }}>#</TableCell>
            <TableCell>Joueur</TableCell>
            <TableCell align="right">{labels.ratioLabel}</TableCell>
            <TableCell align="right">MJ</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              {labels.mainLabel}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {data.items.map((item, index) => {
            const rank = (data.page - 1) * data.limit + index + 1;
            const playerUrl = buildPlayerUrl(item.player);
            const teamUrl = buildTeamUrl(item.team);

            return (
              <TableRow key={item.player?._id || `${item._id}-${index}`} hover>
                <TableCell sx={{ fontWeight: 700 }}>{rank}</TableCell>

                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                   <Avatar
                      src={getPlayerPhoto(item.player)}
                      alt={item.player?.displayName || 'player'}
                      sx={{
                        width: 34,
                        height: 34,
                        bgcolor: 'grey.200',
                        overflow: 'hidden',
                        '& .MuiAvatar-img': {
                          objectFit: 'cover',
                          objectPosition: 'center',
                          transform: 'scale(1.35)',
                        },
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {playerUrl ? (
                          <Link
                            href={playerUrl}
                            underline="hover"
                            color="inherit"
                            onClick={(e) => handleLinkClick(e, playerUrl)}
                          >
                            {item.player?.displayName || '-'}
                          </Link>
                        ) : (
                          item.player?.displayName || '-'
                        )}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        {teamUrl ? (
                          <Link
                            href={teamUrl}
                            underline="hover"
                            color="inherit"
                            onClick={(e) => handleLinkClick(e, teamUrl)}
                          >
                            {item.team?.name || item.team?.fullName || '-'}
                          </Link>
                        ) : (
                          item.team?.name || item.team?.fullName || '-'
                        )}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>

                <TableCell align="right">
                  {formatDecimal(item.valuePerMatch)}
                </TableCell>

                <TableCell align="right">
                  {item.matchesCount ?? '-'}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 800,
                    color: '#2e7d32',
                  }}
                >
                  {item.value ?? 0}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMobileCards = () => (
    <Stack spacing={1.25}>
      {data.items.map((item, index) => {
        const rank = (data.page - 1) * data.limit + index + 1;
        const playerUrl = buildPlayerUrl(item.player);
        const teamUrl = buildTeamUrl(item.team);

        return (
          <Card key={item.player?._id || `${item._id}-${index}`} variant="outlined">
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Typography sx={{ minWidth: 22, fontWeight: 800 }}>
                  {rank}
                </Typography>

                <Avatar
                  src={getPlayerPhoto(item.player)}
                  alt={item.player?.displayName || 'player'}
                 sx={{
                    width: 38,
                    height: 38,
                    bgcolor: 'grey.200',
                    overflow: 'hidden',
                    '& .MuiAvatar-img': {
                      objectFit: 'cover',
                      objectPosition: 'center',
                      transform: 'scale(1.35)',
                    },
                  }}
                >
                  <PersonIcon fontSize="small" />
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                    {playerUrl ? (
                      <Link
                        href={playerUrl}
                        underline="hover"
                        color="inherit"
                        onClick={(e) => handleLinkClick(e, playerUrl)}
                      >
                        {item.player?.displayName || '-'}
                      </Link>
                    ) : (
                      item.player?.displayName || '-'
                    )}
                  </Typography>

                  <Typography variant="caption" color="text.secondary" noWrap>
                    {teamUrl ? (
                      <Link
                        href={teamUrl}
                        underline="hover"
                        color="inherit"
                        onClick={(e) => handleLinkClick(e, teamUrl)}
                      >
                        {item.team?.name || item.team?.fullName || '-'}
                      </Link>
                    ) : (
                      item.team?.name || item.team?.fullName || '-'
                    )}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontWeight: 800, color: '#2e7d32' }}>
                    {item.value ?? 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {labels.mainLabel}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 1 }} />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  {labels.ratioLabel}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {formatDecimal(item.valuePerMatch)}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  MJ
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {item.matchesCount ?? '-'}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );

 return (
  <Box sx={{ pt: 1 }}>
    {loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={26} />
      </Box>
    ) : error ? (
      <Typography color="error">{error}</Typography>
    ) : data.items?.length === 0 ? (
      <Typography color="text.secondary">
        Aucun résultat.
      </Typography>
    ) : (
      <>
        {isMobile ? renderMobileCards() : renderDesktopTable()}

        {data.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
            <Pagination
              page={page}
              count={data.totalPages}
              onChange={(_, value) => setPage(value)}
              size={isMobile ? 'small' : 'medium'}
              siblingCount={isMobile ? 0 : 1}
            />
          </Box>
        )}
      </>
    )}
  </Box>
);
}